import { Button, Icon, Image, InlineStack, Paragraph, Stack, Text } from '@customerio/pluma-components/react';
import { FileInput } from '@/components/file-input';
import type { SignResponse } from '@/services/uploads';
import { isEmptyString } from '@/utils/string';
import type { AttachedSource, ImportSource, SheetSource } from './types';
import { GoogleSheetsSourceButton } from './google-sheets-source-button';
import { DEFAULT_ACCEPTED_EXTENSIONS, fileInputLabel, maxFileSizeLabel, type AcceptedExtension } from './file-rules';
import { useSourceUpload } from './use-source-upload';
import googleSheetsLogo from '@/assets/images/integrations/google-sheets.svg';

export type { AcceptedExtension } from './file-rules';

type ImportSourceFieldProps = {
  value: ImportSource | null;
  onChange: (source: ImportSource | null) => void;
  workspaceId: number;
  maxFileSizeBytes: number | undefined;
  /**
   * Notifies the owner while an upload is in flight. During a replace, `value`
   * still points at the previous key, so a form that only checks for a non-null
   * source would submit stale import data.
   */
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
  /**
   * Signs the upload. Each surface has its own sign endpoint answering its own shape, so the
   * caller adapts it to the shared one rather than this field knowing which endpoint it serves.
   * The returned policy decides the bucket, so it also decides which uploader runs.
   */
  signUpload: (file: { size: number; name: string; contentType: string }) => Promise<SignResponse>;
  /**
   * The file types this surface takes. Narrower than the default for CSV imports, whose pipeline
   * reads only CSV — accepting a JSON there would sign and upload a file it cannot process.
   */
  acceptedExtensions?: readonly AcceptedExtension[];
  /**
   * Something the surface has to say about what it does with the file, shown beside the size
   * limit. Only CSV imports has one — the acceptable-use notice — and it is the surface's to
   * word rather than this field's.
   */
  notice?: React.ReactNode;
  /**
   * Told when the reader throws the attached file away, or asks to swap it for another.
   *
   * The field owns those two controls but not what they mean to the surface: CSV imports reports
   * each as its own event, and collections reports neither. `kind` is what the surface calls the
   * source it is losing, which is the only thing the field knows that the surface does not.
   */
  onSourceDiscarded?: (change: { reason: 'deleted' | 'replaced'; kind: ImportSource['kind'] }) => void;
  /**
   * Closes every control the field owns. The surface says why: CSV imports passes the reader's
   * lack of `journeys/imports/create`, because an upload they could never turn into an import
   * still signs a policy and writes an object into the workspace's bucket.
   */
  isDisabled?: boolean;
  /**
   * The source the import already holds, shown when the reader has not picked a new one.
   *
   * Without it a saved import's upload step showed the empty drop zone, so the file the import
   * was about to be re-read from was nowhere on the screen — on a failed import, on one returned
   * to from the columns steps, and on one whose upload had aged out. The embedded app branches on
   * the record's own `filename` here (`import-csv/upload-file/index.gts`), which is what this
   * carries.
   *
   * A surface creating an import has no record and passes nothing.
   */
  attachedSource?: AttachedSource | null;
  /**
   * Throws away the source the import already holds. Without it Delete is not offered for one,
   * because this field cannot let go of a source it is not holding — and then a saved import is
   * stuck with the kind it has: the attached chip's Replace opens the picker for that kind alone,
   * so the only route to the other kind is the empty field this returns to.
   */
  onClearAttachedSource?: () => void | Promise<void>;
  /**
   * Whether the source this field is showing has failed, which its chip reports with a critical
   * icon in place of the usual check.
   *
   * About the source shown, not the import: the embedded app reads the record's `error` for the
   * same icon but clears that error the moment a replacement is picked
   * (`import-csv/setup/index.gts:421,430`), so a file the reader has just chosen reads as good
   * while the step's error banner is still up. The surface decides, since it is the one that
   * knows which source this is.
   */
  hasFailed?: boolean;
};

// The collections endpoints only ever return the sheet ids (ui_api/collections/index.go), so the
// display names exist solely on a source picked in this session.
function describeSheet(sheet: SheetSource): string {
  if (isEmptyString(sheet.sheet_name) || isEmptyString(sheet.spreadsheet_name)) {
    return `Sheet ID ${sheet.spreadsheet_id}`;
  }
  return `${sheet.sheet_name} from Google Sheet ${sheet.spreadsheet_name}`;
}

export function ImportSourceField({
  value,
  onChange,
  workspaceId,
  maxFileSizeBytes,
  onUploadingChange,
  signUpload,
  acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
  notice,
  onSourceDiscarded,
  isDisabled = false,
  attachedSource = null,
  onClearAttachedSource,
  hasFailed = false,
}: ImportSourceFieldProps) {
  const { isUploading, error, selectFile, selectSheet, clearSource, reportDeleting, reportReplacing } = useSourceUpload(
    {
      onChange,
      onUploadingChange,
      signUpload,
      acceptedExtensions,
      maxFileSizeBytes,
      onSourceDiscarded,
    },
  );
  // Both reasons a control of this field is closed: the surface closed it, or an upload it
  // started is still running.
  const isControlDisabled = isDisabled || isUploading;
  const accept = acceptedExtensions.map((ext) => `.${ext}`).join(',');

  // What is on the field: the source the reader picked in this session, or failing that the one
  // the import already holds.
  const shown = value ?? attachedSource;
  const onDelete = deleteHandler({ value, attachedSource, onClearAttachedSource, clearSource, reportDeleting });

  if (shown == null) {
    return (
      <SourcePicker
        accept={accept}
        acceptedExtensions={acceptedExtensions}
        isDisabled={isControlDisabled}
        error={error}
        maxFileSizeBytes={maxFileSizeBytes}
        notice={notice}
        workspaceId={workspaceId}
        onFileSelected={(file, viaDragAndDrop) => void selectFile(file, viaDragAndDrop)}
        onSelectSheet={selectSheet}
      />
    );
  }

  if (shown.kind === 'sheet') {
    return (
      <AttachedSheet
        sheet={shown.sheet}
        workspaceId={workspaceId}
        isDisabled={isControlDisabled}
        hasFailed={hasFailed}
        onSelect={selectSheet}
        onDelete={onDelete}
        onReplace={() => reportReplacing('sheet')}
      />
    );
  }

  return (
    <AttachedFile
      filename={shown.filename}
      accept={accept}
      isDisabled={isControlDisabled}
      error={error}
      hasFailed={hasFailed}
      onFileSelected={(file, viaDragAndDrop) => void selectFile(file, viaDragAndDrop)}
      onDelete={onDelete}
      onReplace={() => reportReplacing('s3')}
    />
  );
}

/**
 * How the attached source reads: good, or the reason the step is showing an error above it.
 *
 * One component for both chips because the embedded app's icon sits outside its own file/sheet
 * branch and marks either (`import-csv/upload-file/index.gts:236-242`) — a failed sheet import
 * reaches this step the same way a failed file one does.
 */
type SourceStatusIconProps = { hasFailed: boolean };

function SourceStatusIcon({ hasFailed }: SourceStatusIconProps) {
  return hasFailed ? <Icon name="critical" color="critical" /> : <Icon name="check" color="success" />;
}

type DeleteHandlerInput = {
  value: ImportSource | null;
  attachedSource: AttachedSource | null;
  onClearAttachedSource: (() => void | Promise<void>) | undefined;
  clearSource: (kind: ImportSource['kind']) => void;
  reportDeleting: (kind: ImportSource['kind']) => void;
};

/**
 * What Delete does, or null when there is nothing for it to do.
 *
 * The two sources it can be asked to drop are not the same thing. One picked in this session is
 * the field's own, and dropping it is a local matter. One the record holds is the surface's:
 * letting go of that is a reset the server has to be told about, so the field asks rather than
 * acts, and offers the button at all only when it has been given something to ask.
 */
function deleteHandler({
  value,
  attachedSource,
  onClearAttachedSource,
  clearSource,
  reportDeleting,
}: DeleteHandlerInput): (() => void) | null {
  if (value != null) {
    return () => clearSource(value.kind);
  }
  if (attachedSource == null || onClearAttachedSource == null) {
    return null;
  }
  return () => {
    reportDeleting(attachedSource.kind);
    void onClearAttachedSource();
  };
}

type SourcePickerProps = {
  accept: string;
  acceptedExtensions: readonly AcceptedExtension[];
  isDisabled: boolean;
  error: string | null;
  maxFileSizeBytes: number | undefined;
  notice: React.ReactNode;
  workspaceId: number;
  onFileSelected: (file: File, viaDragAndDrop: boolean) => void;
  onSelectSheet: (sheet: SheetSource) => void;
};

/** Nothing attached yet: the two ways to give the field a source, and what it will take. */
function SourcePicker({
  accept,
  acceptedExtensions,
  isDisabled,
  error,
  maxFileSizeBytes,
  notice,
  workspaceId,
  onFileSelected,
  onSelectSheet,
}: SourcePickerProps) {
  return (
    <Stack gap="100">
      <FileInput
        label={fileInputLabel(acceptedExtensions)}
        browseLabel="Choose file"
        accept={accept}
        isDisabled={isDisabled}
        error={error ?? undefined}
        onFileSelected={(file, { viaDragAndDrop }) => onFileSelected(file, viaDragAndDrop)}
        action={<GoogleSheetsSourceButton workspaceId={workspaceId} onSelect={onSelectSheet} isDisabled={isDisabled} />}
      />
      <InlineStack alignInline="space-between" gap="200">
        <Text color="subtle" size="xs">
          {notice}
        </Text>
        {maxFileSizeBytes == null ? null : (
          <Text color="subtle" size="xs" flexShrink="0">
            Maximum size: {maxFileSizeLabel(maxFileSizeBytes)}
          </Text>
        )}
      </InlineStack>
    </Stack>
  );
}

type AttachedFileProps = {
  filename: string;
  accept: string;
  isDisabled: boolean;
  error: string | null;
  /** The import failed, so the chip says so instead of reporting the file as good. */
  hasFailed: boolean;
  onFileSelected: (file: File, viaDragAndDrop: boolean) => void;
  /** Null for a source the field is not holding — see `deleteHandler`. */
  onDelete: (() => void) | null;
  onReplace: () => void;
};

/** The file that is attached, with the ways to be rid of it. */
function AttachedFile({
  filename,
  accept,
  isDisabled,
  error,
  hasFailed,
  onFileSelected,
  onDelete,
  onReplace,
}: AttachedFileProps) {
  return (
    <FileInput
      label={`Replace ${filename}`}
      accept={accept}
      isDisabled={isDisabled}
      error={error ?? undefined}
      onFileSelected={(file, { viaDragAndDrop }) => onFileSelected(file, viaDragAndDrop)}
      variant="plain"
    >
      {({ openFilePicker }) => (
        <Stack gap="200">
          <InlineStack alignBlock="center" gap="150">
            <InlineStack alignBlock="center" gap="100" backgroundColor="subtle" p="200">
              <Icon name="download" />
              {/* A customer's own file name, as the embedded app masks it too
                  (`import-csv/upload-file/index.gts:233`). */}
              <Text className="fs-mask">{filename}</Text>
            </InlineStack>
            <SourceStatusIcon hasFailed={hasFailed} />
          </InlineStack>
          <InlineStack gap="100">
            {onDelete == null ? null : (
              <Button variant="secondary" isCritical={true} isDisabled={isDisabled} onClick={onDelete}>
                Delete file
              </Button>
            )}
            <Button
              variant="secondary"
              isDisabled={isDisabled}
              onClick={() => {
                onReplace();
                openFilePicker();
              }}
            >
              Replace
            </Button>
          </InlineStack>
        </Stack>
      )}
    </FileInput>
  );
}

type AttachedSheetProps = {
  sheet: SheetSource;
  workspaceId: number;
  isDisabled: boolean;
  /** The import failed, so the chip says so instead of reporting the sheet as good. */
  hasFailed: boolean;
  onSelect: (sheet: SheetSource) => void;
  /** Null for a source the field is not holding — see `deleteHandler`. */
  onDelete: (() => void) | null;
  onReplace: () => void;
};

/** The sheet that is attached. No file input: a sheet is picked, not uploaded. */
function AttachedSheet({
  sheet,
  workspaceId,
  isDisabled,
  hasFailed,
  onSelect,
  onDelete,
  onReplace,
}: AttachedSheetProps) {
  return (
    <Stack gap="100">
      <InlineStack alignBlock="center" gap="050">
        <Image src={googleSheetsLogo} alt="" width={16} height={16} />
        <Paragraph className="fs-mask">{describeSheet(sheet)}</Paragraph>
        <SourceStatusIcon hasFailed={hasFailed} />
      </InlineStack>
      <InlineStack gap="100">
        <GoogleSheetsSourceButton
          workspaceId={workspaceId}
          onSelect={onSelect}
          onOpen={onReplace}
          isDisabled={isDisabled}
        >
          Replace
        </GoogleSheetsSourceButton>
        {onDelete == null ? null : (
          <Button variant="secondary" isDisabled={isDisabled} onClick={onDelete}>
            Remove
          </Button>
        )}
      </InlineStack>
    </Stack>
  );
}
